<script lang="ts" setup>
  import { computed, useId } from 'vue';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { FontIcon } from '../FontIcon';
  import {
    containerClass,
    containerVariantClass,
    iconClass,
    textClass,
  } from './InlineAlert.css';
  import type {
    InlineAlertProps,
    InlineAlertSlots,
    InlineAlertVariantType,
  } from './InlineAlert.types';
  import { InlineAlertVariant } from './InlineAlert.types';

  const inlineAlertIconMap: Record<InlineAlertVariantType, TUNDR_ICONS> = {
    [InlineAlertVariant.INFO]: TUNDR_ICONS.INFO,
    [InlineAlertVariant.WARNING]: TUNDR_ICONS.EXCLAMATION_TRIANGLE,
    [InlineAlertVariant.ERROR]: TUNDR_ICONS.ERROR,
  };

  const props = withDefaults(defineProps<InlineAlertProps>(), {
    variant: InlineAlertVariant.INFO,
  });

  defineSlots<InlineAlertSlots>();

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-inline-alert-${uid}`,
  );

  const containerClasses = computed(() => [
    containerClass,
    containerVariantClass[props.variant],
  ]);

  const resolvedIconName = computed(
    () => props.iconName ?? inlineAlertIconMap[props.variant],
  );

  const isAssertive = computed(
    () =>
      props.variant === InlineAlertVariant.ERROR ||
      props.variant === InlineAlertVariant.WARNING,
  );

  const ariaRole = computed(() => (isAssertive.value ? 'alert' : 'status'));

  const ariaLive = computed(() => (isAssertive.value ? 'assertive' : 'polite'));
</script>

<template>
  <div
    :id="id"
    :class="containerClasses"
    :role="ariaRole"
    :aria-live="ariaLive"
    :data-testid="resolvedTestId"
  >
    <span
      :class="iconClass"
      aria-hidden="true"
      :data-testid="`${resolvedTestId}-icon`"
    >
      <FontIcon :icon-name="resolvedIconName" />
    </span>

    <span :class="textClass" :data-testid="`${resolvedTestId}-text`">
      <slot>{{ text }}</slot>
    </span>
  </div>
</template>
